import { useCallback, useEffect, useState } from 'react'
import type { PostHog } from 'posthog-js'
import type { ConsentStatus } from '@/types'
import { CONSENT_STORAGE_KEY } from '@/types'
import { shouldInitializePostHog, usePostHog } from '@/lib/posthog'

/**
 * Get consent status from PostHog or localStorage fallback
 * Handles case where PostHog is blocked by adblocker or re-initialized
 * 
 * IMPORTANT: This function should only be called client-side (in useEffect or event handlers)
 * to avoid hydration mismatches. Server always returns 'pending'.
 * 
 * Priority: localStorage > PostHog
 * This ensures consent persists even if PostHog re-initializes after login
 */
function getConsentStatus(
  posthog: PostHog | undefined,
  mode: string,
  devEnabled?: string,
): ConsentStatus {
  // During SSR, localStorage is not available - return pending
  if (typeof window === 'undefined') {
    return 'pending'
  }

  // Check localStorage first (persists across PostHog re-initializations)
  const stored = localStorage.getItem(CONSENT_STORAGE_KEY)
  if (stored === 'granted' || stored === 'denied') {
    // If PostHog should be initialized and is available, sync localStorage state to PostHog
    // This restores consent after PostHog re-initializes (e.g., after login)
    if (posthog && shouldInitializePostHog(mode, devEnabled)) {
      try {
        const posthogStatus = posthog.get_explicit_consent_status()
        // If PostHog has 'pending' but localStorage has consent, restore it
        if (posthogStatus === 'pending' && stored === 'granted') {
          posthog.opt_in_capturing()
        } else if (posthogStatus === 'pending' && stored === 'denied') {
          posthog.opt_out_capturing()
        }
      } catch {
        // PostHog may be blocked by adblocker - localStorage is already set
      }
    }
    return stored
  }

  // No localStorage value - check PostHog if available
  if (posthog && shouldInitializePostHog(mode, devEnabled)) {
    try {
      const posthogStatus = posthog.get_explicit_consent_status()
      // Only trust PostHog if it has a non-pending status
      if (posthogStatus !== 'pending') {
        return posthogStatus
      }
    } catch {
      // PostHog may be blocked by adblocker - fall through to pending
    }
  }

  return 'pending'
}

/**
 * Hook for managing cookie consent state
 *
 * Features:
 * - SSR-safe: returns consistent 'pending' state during SSR to avoid hydration mismatches
 * - Prioritizes localStorage to persist consent across PostHog re-initializations
 * - Syncs localStorage state to PostHog when PostHog re-initializes (e.g., after login)
 * - Falls back to PostHog if localStorage is empty
 * - Only shows banner in production (where PostHog is active)
 * - Handles PostHog initialization timing and adblocker scenarios
 */
export function useCookieConsent() {
  const posthog = usePostHog()
  const mode = import.meta.env.MODE || 'development'
  const devEnabled = import.meta.env.VITE_POSTHOG_DEV_ENABLED
  const shouldInitPostHog = shouldInitializePostHog(mode, devEnabled)

  // SSR-safe initial state: always start with false/pending to match server render
  // This prevents hydration mismatches since localStorage isn't available on the server
  const [showBanner, setShowBanner] = useState<boolean>(false)
  
  // Store consentStatus in state to avoid hydration mismatch
  // Server always renders 'pending', client updates in useEffect after hydration
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>('pending')

  // Sync state after hydration (client-side only)
  // This effect runs after the initial render, updating state based on localStorage/PostHog
  useEffect(() => {
    if (!shouldInitPostHog) {
      setShowBanner(false)
      setConsentStatus('pending')
      return
    }

    const status = getConsentStatus(posthog, mode, devEnabled)
    setConsentStatus(status)
    setShowBanner(status === 'pending')
  }, [posthog, mode, devEnabled, shouldInitPostHog])

  // Memoized function to refresh consent status (useful after user interaction)
  const refreshConsentStatus = useCallback(() => {
    if (shouldInitPostHog) {
      const status = getConsentStatus(posthog, mode, devEnabled)
      setConsentStatus(status)
      setShowBanner(status === 'pending')
    }
  }, [posthog, mode, devEnabled, shouldInitPostHog])

  return {
    showBanner,
    setShowBanner,
    consentStatus,
    refreshConsentStatus,
  }
}
